import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { t } from '../i18n'
import { api } from '../Services/api'
import {
  commentToken,
  saveCommentName,
  savedCommentName,
  useDiscussions,
  withCommentToken,
} from '../State/Discussions'
import { useUi } from '../State/Ui'
import { track } from '../Utils/analytics'
import { timeAgo } from '../Utils/time'
import ChipRow from './ChipRow'
import { ChatIcon, Spinner } from './Icons'
import { SourceAvatar } from './SourceAvatar'

/* Komentari. A story has one thread: a comment is written
   under one article (Klix's, Avaz's, …) and says so, but readers of every
   source's version of the story read and answer each other in one place. */

export const THREAD_ID = 'komentari'
const BODY_MAX = 1000
const NAME_MAX = 30
const AVATAR_COLORS = ['#fde8d4', '#e3eefc', '#e4f4e6', '#f6e3f3', '#fff1c7', '#e6e7fb']

function scrollToThread(behavior = 'smooth') {
  document.getElementById(THREAD_ID)?.scrollIntoView({ behavior, block: 'start' })
}

/* On a card: the story's comment count, opens its thread (spec-free experiment). */
export function CommentButton({ story, from, here = false }) {
  if (story.commentCount == null) return null
  const label = t('comments.open', { count: story.commentCount })
  const content = (
    <>
      <ChatIcon size={19} />
      {story.commentCount > 0 && <span>{story.commentCount}</span>}
    </>
  )
  if (here) {
    return (
      <a
        href={`#${THREAD_ID}`}
        className="comment-button"
        aria-label={label}
        onClick={(e) => {
          e.preventDefault()
          scrollToThread()
        }}
      >
        {content}
      </a>
    )
  }
  return (
    <Link
      to={`/prica/${story.id}#${THREAD_ID}`}
      state={{ articleId: story.articleId }}
      className="comment-button"
      aria-label={label}
      onClick={() => track('comments_open', { from })}
    >
      {content}
    </Link>
  )
}

/* Under a card on Diskusije: the newest comment and where it was written. */
export function LastComment({ story, from }) {
  const c = story.lastComment
  if (!c) return null
  return (
    <Link
      to={`/prica/${story.id}#${THREAD_ID}`}
      state={{ articleId: story.articleId }}
      className="last-comment"
      onClick={() => track('comments_open', { from, via: 'last_comment' })}
    >
      <span className="last-comment-head">
        <strong>{c.author}</strong>
        <span className="comment-origin-label">{t('comments.under')}</span>
        <SourceAvatar source={c.article.source} size={16} />
        <span className="comment-origin-name">{c.article.source.name}</span>
        <span className="card-dot" aria-hidden="true">
          ·
        </span>
        <time dateTime={c.createdAt}>{timeAgo(c.createdAt)}</time>
      </span>
      <span className="last-comment-body">{c.body}</span>
    </Link>
  )
}

function avatarColor(name) {
  let h = 0
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

function errorText(error, fallback) {
  const status = error?.response?.status
  const message = error?.response?.data?.error
  // 400/429 carry a sentence written for the reader.
  return (status === 400 || status === 429) && message ? message : fallback
}

function useThread(storyId) {
  const [state, setState] = useState({ status: 'loading', data: null })
  useEffect(() => {
    let alive = true
    setState({ status: 'loading', data: null })
    api
      .storyComments(storyId, commentToken())
      .then((data) => alive && setState({ status: 'ready', data }))
      .catch(() => alive && setState({ status: 'error', data: null }))
    return () => {
      alive = false
    }
  }, [storyId])
  return [state, setState]
}

function countBySource(comments) {
  const map = new Map()
  comments.forEach((c) => {
    const s = c.article.source
    const entry = map.get(s.slug) || { source: s, count: 0 }
    entry.count += 1
    map.set(s.slug, entry)
  })
  return [...map.values()].sort((a, b) => b.count - a.count)
}

function Composer({ storyId, articles, defaultArticleId, onPosted }) {
  const { notify } = useUi()
  const [name, setName] = useState(savedCommentName)
  const [body, setBody] = useState('')
  const [open, setOpen] = useState(false)
  const [articleId, setArticleId] = useState(() =>
    articles.some((a) => a.id === defaultArticleId) ? defaultArticleId : articles[0]?.id
  )
  const [posting, setPosting] = useState(false)
  const [error, setError] = useState(null)
  const article = articles.find((a) => a.id === articleId) || articles[0]
  const canPost = name.trim().length > 0 && body.trim().length >= 2 && !posting

  const submit = async (e) => {
    e.preventDefault()
    if (!canPost) return
    setPosting(true)
    setError(null)
    try {
      const comment = await withCommentToken((token) =>
        api.postComment(storyId, { articleId: article.id, name: name.trim(), body }, token)
      )
      saveCommentName(comment.author)
      setBody('')
      onPosted(comment)
      notify(t('comments.posted'))
      track('comment_post', { source: article.source.slug, length: body.length })
    } catch (err) {
      setError(errorText(err, t('comments.postError')))
    } finally {
      setPosting(false)
    }
  }

  return (
    <form className={`comment-form ${open ? 'open' : ''}`} onSubmit={submit}>
      <textarea
        className="comment-input"
        value={body}
        rows={open ? 3 : 1}
        maxLength={BODY_MAX}
        placeholder={t('comments.placeholder')}
        aria-label={t('comments.placeholder')}
        onFocus={() => setOpen(true)}
        onChange={(e) => setBody(e.target.value)}
      />
      {open && (
        <>
          <div className="comment-form-row">
            <input
              className="comment-name"
              value={name}
              maxLength={NAME_MAX}
              placeholder={t('comments.name')}
              aria-label={t('comments.name')}
              autoComplete="nickname"
              onChange={(e) => setName(e.target.value)}
            />
            {articles.length > 1 && (
              <label className="comment-article">
                <span>{t('comments.underArticle')}</span>
                <SourceAvatar source={article.source} size={18} />
                <select
                  value={article.id}
                  onChange={(e) => setArticleId(Number(e.target.value))}
                  aria-label={t('comments.underArticleLabel')}
                >
                  {articles.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.source.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="comment-form-foot">
            <p className="comment-note">{t('comments.note')}</p>
            {body.length > BODY_MAX - 150 && (
              <span className="comment-left">{BODY_MAX - body.length}</span>
            )}
            <button type="submit" className="btn btn-primary btn-small" disabled={!canPost}>
              {posting ? <Spinner size={14} /> : t('comments.submit')}
            </button>
          </div>
          {error && (
            <p className="comment-error" role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </form>
  )
}

function Comment({ comment, onDelete, onReport }) {
  const c = comment
  return (
    <li className="comment">
      <span className="comment-avatar" style={{ background: avatarColor(c.author) }} aria-hidden="true">
        {c.author.slice(0, 1).toUpperCase()}
      </span>
      <div className="comment-main">
        <div className="comment-head">
          <strong className="comment-author">{c.author}</strong>
          {c.mine && <span className="tag-following">{t('comments.you')}</span>}
          <span className="card-dot" aria-hidden="true">
            ·
          </span>
          <time dateTime={c.createdAt}>{timeAgo(c.createdAt)}</time>
        </div>
        {/* Where it was written: the article (and source) it was posted under. */}
        <a
          className="comment-origin"
          href={c.article.url}
          target="_blank"
          rel="noopener noreferrer"
          title={c.article.title}
          aria-label={t('comments.originLabel', { name: c.article.source.name, title: c.article.title })}
        >
          <span className="comment-origin-label">{t('comments.under')}</span>
          <SourceAvatar source={c.article.source} size={16} />
          <span className="comment-origin-name">{c.article.source.name}</span>
        </a>
        <p className="comment-body">{c.body}</p>
        <div className="comment-actions">
          {c.mine ? (
            <button type="button" className="btn-plain" onClick={() => onDelete(c)}>
              {t('comments.delete')}
            </button>
          ) : (
            <button type="button" className="btn-plain" onClick={() => onReport(c)}>
              {t('comments.report')}
            </button>
          )}
        </div>
      </div>
    </li>
  )
}

/* The story's thread on the story page: filter by where comments were
   written, write one (under an article you choose), delete your own,
   report others'. */
function Thread({ storyId, defaultArticleId }) {
  const { notify } = useUi()
  const location = useLocation()
  const [{ status, data }, setState] = useThread(storyId)
  const [filter, setFilter] = useState('all')
  const scrolled = useRef(false)

  useEffect(() => {
    if (status !== 'ready' || scrolled.current || location.hash !== `#${THREAD_ID}`) return
    scrolled.current = true
    scrollToThread('auto')
  }, [status, location.hash])

  const comments = useMemo(() => data?.comments || [], [data])
  const bySource = useMemo(() => countBySource(comments), [comments])
  const shown = filter === 'all' ? comments : comments.filter((c) => c.article.source.slug === filter)
  const update = (fn) => setState((s) => ({ ...s, data: { ...s.data, comments: fn(s.data.comments) } }))

  const remove = async (c) => {
    if (!window.confirm(t('comments.confirmDelete'))) return
    try {
      await api.deleteComment(c.id, commentToken())
      update((list) => list.filter((x) => x.id !== c.id))
      notify(t('comments.deleted'))
    } catch (e) {
      notify(t('comments.actionError'))
    }
  }

  const report = async (c) => {
    try {
      await withCommentToken((token) => api.reportComment(c.id, token))
      update((list) => list.filter((x) => x.id !== c.id)) // hidden for whoever reported it
      notify(t('comments.reported'))
      track('comment_report', {})
    } catch (e) {
      notify(t('comments.actionError'))
    }
  }

  return (
    <section className="thread" id={THREAD_ID} aria-labelledby="thread-title">
      <div className="thread-head">
        <h2 className="thread-title" id="thread-title">
          <ChatIcon size={20} />
          {t('comments.title')}
          {comments.length > 0 && <span className="thread-count">{comments.length}</span>}
        </h2>
        <span className="badge-experiment">{t('discussions.experiment')}</span>
      </div>
      {status === 'ready' && data.articles.length > 1 && (
        <p className="thread-intro">
          {t('comments.intro', { count: data.articles.length })}
        </p>
      )}

      {status === 'loading' && (
        <div className="load-more">
          <Spinner size={22} />
        </div>
      )}
      {status === 'error' && <p className="thread-empty">{t('comments.loadError')}</p>}

      {status === 'ready' && (
        <>
          <Composer
            storyId={storyId}
            articles={data.articles}
            defaultArticleId={defaultArticleId}
            onPosted={(comment) => {
              update((list) => [comment, ...list])
              setFilter('all')
            }}
          />
          {bySource.length > 1 && (
            <ChipRow label={t('comments.filter')} className="thread-chips" activeKey={filter}>
              {[{ source: null, count: comments.length }, ...bySource].map(({ source, count }) => {
                const key = source ? source.slug : 'all'
                return (
                  <button
                    key={key}
                    type="button"
                    className={`chip ${filter === key ? 'chip-active' : ''}`}
                    aria-pressed={filter === key}
                    onClick={() => setFilter(key)}
                  >
                    {source && <SourceAvatar source={source} size={18} />}
                    {source ? source.name : t('comments.all')}
                    <span className="chip-count">{count}</span>
                  </button>
                )
              })}
            </ChipRow>
          )}
          {comments.length === 0 ? (
            <p className="thread-empty">{t('comments.empty')}</p>
          ) : (
            <ul className="comment-list">
              {shown.map((c) => (
                <Comment key={c.id} comment={c} onDelete={remove} onReport={report} />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}

export default function StoryComments({ storyId, defaultArticleId }) {
  const { enabled } = useDiscussions()
  const location = useLocation()
  if (!enabled) return null
  return (
    <Thread
      key={storyId}
      storyId={storyId}
      defaultArticleId={location.state?.articleId ?? defaultArticleId}
    />
  )
}
